/**
 * The prominent start/duration strip on an itinerary stop card, plus its
 * empty-state affordance ("+ Set start & duration") shown when the stop has
 * neither value and the card is editable.
 *
 * Extracted verbatim from `ItineraryStopCard` (#314) — presentation only, no
 * behaviour of its own beyond delegating the empty-state click.
 */

const formatStartTime = (hhmm: string): string => {
  // Accept "HH:MM" 24h input; render in the user-facing 12h form so the
  // card reads naturally regardless of how the value was entered.
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return hhmm;
  const h = Math.max(0, Math.min(23, parseInt(m[1], 10)));
  const min = m[2];
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${min} ${period}`;
};

const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

interface StopTimingStripProps {
  arrivalTime?: string;
  durationMinutes?: number | null;
  readOnly: boolean;
  onEditDetails: () => void;
}

export const StopTimingStrip = ({
  arrivalTime,
  durationMinutes,
  readOnly,
  onEditDetails,
}: StopTimingStripProps) => {
  if (!(arrivalTime || durationMinutes)) {
    if (readOnly) return null;
    return (
      <button
        type="button"
        onClick={onEditDetails}
        className="mt-2 text-xs italic text-stone-500 hover:text-primary-700 self-start"
      >
        + Set start &amp; duration
      </button>
    );
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      {arrivalTime && (
        <span className="inline-flex items-baseline gap-1.5">
          <span className="text-[0.65rem] font-sans uppercase tracking-widest text-gray-500">
            Start
          </span>
          <span className="font-serif text-base text-gray-900">
            {formatStartTime(arrivalTime)}
          </span>
        </span>
      )}
      {durationMinutes != null && (
        <span className="inline-flex items-baseline gap-1.5">
          <span className="text-[0.65rem] font-sans uppercase tracking-widest text-gray-500">
            Duration
          </span>
          <span className="font-serif text-base text-gray-900">
            {formatDuration(durationMinutes)}
          </span>
        </span>
      )}
    </div>
  );
};
